import { ContentPage } from "@/components/marketing/ContentPage";
import layout from "@/components/marketing/ContentPage.module.css";

export function About() {
  return (
    <ContentPage
      kicker="About"
      title="The operating system for on-brand social posting."
      lead="Notamen learns a brand once, then helps a team draft, review, approve, and schedule posts that sound like that brand — across Instagram, Facebook, and TikTok."
    >
      <h2>What it is</h2>
      <p>
        Notamen is a multi-tenant social-media operations tool. Each
        brand gets its own <strong>brand brain</strong>: a structured profile
        of voice, audience, products or services, platform rules, and safety
        guardrails. The brand brain is the source of truth every draft is
        generated from.
      </p>
      <p>
        From that profile, the app generates on-brand draft posts. A team then
        reviews each draft, approves it, and schedules it to publish across
        Instagram, Facebook, and TikTok. Nothing goes out automatically — a
        person approves every post before it can be scheduled.
      </p>

      <h2>How a post moves through the system</h2>
      <ol>
        <li>
          <strong>Learn the brand.</strong> You build the brand brain — voice,
          audience, products or services, and per-platform rules like caption
          length and hashtag limits.
        </li>
        <li>
          <strong>Generate drafts.</strong> The generator produces draft posts
          from the brand brain, tailored to the platforms you ask for.
        </li>
        <li>
          <strong>Review and approve.</strong> Drafts land in an approval queue.
          Server-side safety-rail checks run on every draft, and a draft that
          trips a blocking check can't be approved without an explicit override
          and a recorded reason.
        </li>
        <li>
          <strong>Schedule and publish.</strong> Approved drafts are scheduled,
          then published to the connected platform accounts.
        </li>
      </ol>

      <h2>Built on Claude</h2>
      <p>
        Draft generation runs on Anthropic's Claude models, accessed through
        the Anthropic API and AWS Bedrock. The brand brain is sent as a cached
        system context so repeated generations for the same brand stay
        efficient.
      </p>

      <h2>Brands of different kinds</h2>
      <p>
        Not every brand sells products. Notamen supports ecommerce,
        service, creator, and media brands, and only asks for — and only
        generates around — the information that a given kind of brand actually
        has. A creator brand with no product catalog doesn't get a product
        section forced on it.
      </p>

      <h2>Where we are today</h2>
      <ul className={layout.cards}>
        <li className={layout.card}>
          <p className={layout.cardTitle}>Invite-only private beta</p>
          <p className={layout.cardBody}>
            Access is currently invite-only. There is no public signup — new
            workspaces are onboarded in waves so we can keep onboarding
            hands-on.
          </p>
        </li>
        <li className={layout.card}>
          <p className={layout.cardTitle}>Built on AWS</p>
          <p className={layout.cardBody}>
            The product runs on AWS in us-east-1, with per-workspace data
            isolation built into the data model from the ground up.
          </p>
        </li>
      </ul>

      <h2>Want in?</h2>
      <p>
        We're inviting new workspaces off the waitlist in small batches. You can{" "}
        <a href="/signup">join the waitlist</a> to be notified when access opens,
        or <a href="/pricing">see how pricing works</a>.
      </p>
    </ContentPage>
  );
}

export default About;
